import { useEffect, useRef } from 'react';

export interface LoadSentinelProps {
  onReachEnd: () => void;
  disabled: boolean;
  rootMargin?: string;
  rootRef?: React.RefObject<HTMLElement | null>;
  dataTestId?: string;
}

const DEFAULT_ROOT_MARGIN = '800px 0px 800px 0px';
const DEFAULT_TEST_ID = 'load-sentinel';

export const LoadSentinel: React.FC<LoadSentinelProps> = ({
  onReachEnd,
  disabled,
  rootMargin = DEFAULT_ROOT_MARGIN,
  rootRef,
  dataTestId = DEFAULT_TEST_ID,
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const onReachEndRef = useRef(onReachEnd);
  onReachEndRef.current = onReachEnd;

  useEffect(() => {
    if (disabled) return;
    const node = sentinelRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onReachEndRef.current();
            break;
          }
        }
      },
      { root: rootRef?.current ?? null, rootMargin, threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [disabled, rootMargin, rootRef]);

  return (
    <div
      ref={sentinelRef}
      aria-hidden="true"
      tabIndex={-1}
      data-testid={dataTestId}
      style={{ width: '100%', height: '1px' }}
    />
  );
};

LoadSentinel.displayName = 'LoadSentinel';
